<script lang="ts" setup>
import { useInput, showRequiredMark, withCommonElInputProps } from '@/forms/input';
import { computed, PropType, ref } from 'vue';
import { OptionType, Role } from '@/types';
import { ElClipToAnchorPosition } from '@/ElClipToAnchor.vue';
import { AutocompleteOptionType, isAutocompleteOptionType } from '@/forms/utils';
import ElDropdown from '@/ElDropdown.vue';
import ElAvatar from '@/ElAvatar.vue';
import ElIcon from '@/ElIcon.vue';
import ElInputContainer from '@/forms/ElInputContainer.vue';
import _ from 'lodash';

export type OptionValueType = string | string[] | null | undefined;

const props = defineProps({
  ...withCommonElInputProps(),
  modelValue: {
    // TODO: it should differ between the single input select and the multiselect
    type: [Array, String] as PropType<OptionValueType>,
    required: false,
    default: undefined,
  },
  placeholder: {
    type: String,
    default: '',
    required: false,
  },
  options: {
    type: Array as PropType<(OptionType | AutocompleteOptionType)[]>,
    required: true,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  noRemoveValue: {
    type: Boolean,
    default: false,
  },
  positionToAnchor: {
    type: String as PropType<ElClipToAnchorPosition>,
    required: false,
    default: 'bottom',
  },
});

const allOptions = computed<InstanceType<typeof ElDropdown>['$props']['items']>(() => {
  const options = props.options.map(o => {
    const option: InstanceType<typeof ElDropdown>['$props']['items'][number] = {
      text: o.label,
      semiboldText: 'semiboldText' in o ? o.semiboldText : undefined,
      avatar: o.imageUrl ? { avatarUrl: o.imageUrl, role: Role.Patient } : undefined,
      onClick: () => setValue(props.multiple ? _.xor(value.value, [o.value]) : o.value),
      check: props.multiple ? props.modelValue?.includes(o.value) : o.value == props.modelValue,
    };

    if (isAutocompleteOptionType(o) && o.prefixIcon?.name) {
      option.leadingIcon = o.prefixIcon?.name ? { name: o.prefixIcon.name, solid: o.prefixIcon.isSolid } : undefined;
    }

    return option;
  });

  if (!props.noRemoveValue && !props.multiple) {
    options.unshift({
      text: '-',
      onClick: () => setValue(undefined),
    });
  }

  if (props.multiple) {
    options.unshift({
      text: value.value?.length === props.options.length ? 'Deseleziona tutto' : 'Seleziona tutto',
      onClick: () => {
        if (value.value?.length === props.options.length) setValue(undefined);
        else setValue(props.options.map(opt => opt.value).slice(0));
        dropdown.value?.hide();
      },
    });
  }

  return options;
});

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (event: 'update:modelValue', newValue: OptionValueType): void;
  // eslint-disable-next-line no-unused-vars
  (event: 'update:modelLabel', newValue: OptionValueType): void;
}>();

const button = ref<HTMLElement>();
const dropdown = ref<InstanceType<typeof ElDropdown>>();

const computedModelValue = computed(() => props.modelValue);
const computedValidation = computed(() => [props.validation]);

const { errorMessage, value, setValue } = useInput(computedModelValue, computedValidation, props.name, newValue => {
  emit('update:modelValue', newValue);
  emit('update:modelLabel', getLabelForOptionValue(newValue));
});

const valueIsEmpty = computed<boolean>(() => {
  return !value.value || !getLabelForOptionValue(value.value);
});

const getLabelForOptionValue = (value: OptionValueType): OptionValueType => {
  if (!value) return;
  if (typeof value === 'string') {
    return props.options?.find(o => o.value === value)?.label || '';
  } else {
    // return list of labels
    return value
      .map(v => {
        const option = props.options.find(o => o.value === v);
        return option ? option.label : '';
      })
      .filter(entry => !!entry);
  }
};

const valueLabel = computed<OptionValueType>(() => {
  return getLabelForOptionValue(value.value);
});

const valueAvatar = computed(() => {
  if (!value.value || props.multiple) return;
  if (typeof value.value === 'string') {
    return props.options?.find(o => o.value === value.value)?.imageUrl;
  }
  return '';
});

const toggleDropdownAndGrabFocus = (toggle: () => void) => {
  toggle();
  button.value?.focus();
};

const closeDropdownOnBlur = (e: FocusEvent, hide: () => void) => {
  // options dropdown must have an option role, so if it ain't that, close the dropdown
  if ((e.relatedTarget as HTMLElement)?.role !== 'option') {
    hide();
  }
};

const showDropdownIfClosed = (e: Event, isOpen: boolean, show: () => void) => {
  if (!isOpen) {
    show();

    e.preventDefault();
    e.stopPropagation();
  }
};
</script>

<template>
  <ElInputContainer
    v-bind="$attrs"
    :name="label"
    :label="label"
    :error-message="props.errorMessage === false ? false : props.errorMessage || errorMessage"
    :hidden-error-message="hiddenErrorMessage"
    :set-required-mark="showRequiredMark([props.validation])"
  >
    <template #default="{ error }">
      <ElDropdown ref="dropdown" :items="allOptions" :multiple="multiple">
        <template #anchor="{ show, hide, toggle, isOpen }">
          <button
            ref="button"
            type="button"
            :name="props.name"
            :disabled="disabled"
            class="el-input !pr-8 px-3"
            :class="{ error, empty: valueIsEmpty, 'focus:ring-1': !error }"
            aria-haspopup="listbox"
            aria-expanded="true"
            aria-labelledby="listbox-label"
            :data-cy="`${label.trim().replaceAll(' ', '').replaceAll('.', '')}-select`"
            @mousedown.prevent="toggleDropdownAndGrabFocus(toggle)"
            @focus.prevent="show"
            @blur.prevent="closeDropdownOnBlur($event, hide)"
            @keydown.enter="showDropdownIfClosed($event, isOpen, show)"
          >
            <span v-if="placeholder && valueIsEmpty" class="text-neutral-inactive truncate w-full block">
              {{ placeholder }}
            </span>
            <span v-else class="flex h-full w-full items-center justify-start truncate">
              <ElAvatar v-if="valueAvatar" :picture="valueAvatar" size="xxs" />
              <span class="truncate">{{ Array.isArray(valueLabel) ? valueLabel.join(', ') : valueLabel }}</span>
            </span>
            <span v-if="!error" class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ElIcon
                name="ChevronDownIcon"
                solid
                class="h-5 w-5 transition-transform duration-200"
                :class="{ 'rotate-180': isOpen, 'text-neutral-inactive': valueIsEmpty, 'text-neutral-darker': !valueIsEmpty }"
              />
            </span>
          </button>
        </template>
      </ElDropdown>
    </template>
  </ElInputContainer>
</template>
